import axiosService from '../../../utils/axios';
import { CatalogGroup } from './emptyPreview';

const CATALOG_TTL_MS = 6 * 60 * 60 * 1000;
const CATALOG_STORAGE_KEY = 'jamwith.influencePreviewCatalog';

interface StoredCatalog {
    seed: number;
    savedAt: number;
    groups: CatalogGroup[];
}

let memoryCatalog: StoredCatalog | null = null;
let catalogRequest: Promise<CatalogGroup[]> | null = null;

function catalogIsFresh(entry: StoredCatalog | null): entry is StoredCatalog {
    return !!entry && entry.groups.length > 0 && Date.now() - entry.savedAt < CATALOG_TTL_MS;
}

function readStoredCatalog(): StoredCatalog | null {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(CATALOG_STORAGE_KEY) || 'null') as StoredCatalog | null;
        if (!parsed || !Array.isArray(parsed.groups)) return null;
        return parsed;
    } catch {
        return null;
    }
}

function writeStoredCatalog(entry: StoredCatalog) {
    try {
        sessionStorage.setItem(CATALOG_STORAGE_KEY, JSON.stringify(entry));
    } catch {
        // The in-memory copy still covers this visit.
    }
}

function normalizeCatalog(value: unknown): CatalogGroup[] {
    const groups = (value as { groups?: unknown })?.groups;
    if (!Array.isArray(groups)) return [];
    return groups.flatMap((group) => {
        const entry = group as CatalogGroup;
        if (!entry?.genre || !Array.isArray(entry.artists)) return [];
        const artists = entry.artists.filter((artist) => artist?.id && artist?.name && artist?.image);
        return artists.length ? [{ genre: entry.genre, artists }] : [];
    });
}

function matchesVisit(entry: StoredCatalog | null, seed: number): entry is StoredCatalog {
    return catalogIsFresh(entry) && entry.seed === seed;
}

/** The five genres already chosen for this visit. */
export function peekPreviewCatalog(seed: number): CatalogGroup[] | null {
    if (matchesVisit(memoryCatalog, seed)) return memoryCatalog.groups;
    const stored = readStoredCatalog();
    if (!matchesVisit(stored, seed)) return null;
    memoryCatalog = stored;
    return stored.groups;
}

/** One request per visit. The server returns five stored genres for this seed. */
export function loadPreviewCatalog(seed: number): Promise<CatalogGroup[]> {
    if (matchesVisit(memoryCatalog, seed)) return Promise.resolve(memoryCatalog.groups);
    const stored = readStoredCatalog();
    if (matchesVisit(stored, seed)) {
        memoryCatalog = stored;
        return Promise.resolve(stored.groups);
    }
    if (catalogRequest) return catalogRequest;
    catalogRequest = Promise.resolve()
        .then(() => axiosService.get(`/user/onboarding/influence-preview?seed=${encodeURIComponent(seed)}`))
        .then((response) => {
            const groups = normalizeCatalog(response?.data);
            if (groups.length) {
                memoryCatalog = { seed, savedAt: Date.now(), groups };
                writeStoredCatalog(memoryCatalog);
            }
            return groups.length ? groups : [];
        })
        .catch(() => (matchesVisit(memoryCatalog, seed) ? memoryCatalog.groups : []))
        .finally(() => {
            catalogRequest = null;
        });
    return catalogRequest;
}
